import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, GitMerge, RefreshCw, ShieldAlert, Download } from "lucide-react";
import { toast } from "sonner";

function exportDuplicatesCsv(groups) {
  const rows = [["Colección", "Grupo", "Nombre", "ID"]];
  if (groups.length === 0) {
    rows.push([`Sin candidatos detectados — generado ${new Date().toISOString()}`, "", "", ""]);
  }
  groups.forEach((g, gi) => {
    g.items.forEach((item) => {
      rows.push([g.collection === "initiatives" ? "Iniciativas" : "Partners/Ecosistema", `Grupo ${gi + 1} (${g.folded_name})`, item.name, item.id]);
    });
  });
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `duplicados-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DuplicateReviewPage() {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = () => {
    setLoading(true);
    api.getDuplicateCandidates(token).then((d) => setGroups(d.items || [])).finally(() => setLoading(false));
  };

  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const rescan = async () => {
    setScanning(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/cross-link/find-duplicate-candidates`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Escaneo de duplicados actualizado");
      load();
    } catch {
      toast.error("Error al escanear duplicados");
    } finally {
      setScanning(false);
    }
  };

  const merge = async (group, keepId, mergeId) => {
    setMerging(mergeId);
    try {
      await api.mergeDuplicate(token, { collection: group.collection, keep_id: keepId, merge_id: mergeId });
      toast.success("Fusionado correctamente");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al fusionar");
    } finally {
      setMerging(null);
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="duplicate-review-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><GitMerge className="w-7 h-7" />Revisión de duplicados</h1>
          <p className="text-muted-foreground mt-1">
            Candidatos detectados por nombre casi-idéntico. Nunca se fusiona nada solo — cada par se confirma manualmente aquí.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportDuplicatesCsv(groups)} data-testid="export-duplicates-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={rescan} disabled={scanning} data-testid="rescan-duplicates-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? "animate-spin" : ""}`} />
            Volver a escanear
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
        <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>Al fusionar, el registro descartado se guarda primero en un histórico de auditoría (no se borra sin dejar rastro) y luego se elimina.</span>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="duplicates-empty">Sin candidatos a duplicado pendientes de revisión.</p>
      ) : (
        <div className="space-y-4" data-testid="duplicates-list">
          {groups.map((g, gi) => (
            <Card key={gi} data-testid={`duplicate-group-${gi}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Badge variant="outline">{g.collection === "initiatives" ? "Iniciativas" : "Partners/Ecosistema"}</Badge>
                  "{g.folded_name}"
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {g.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 border rounded-md p-3">
                    <p className="font-medium text-sm">{item.name}</p>
                    <div className="flex gap-2">
                      {g.items.filter((o) => o.id !== item.id).map((other) => (
                        <Button
                          key={other.id}
                          size="sm"
                          variant="outline"
                          disabled={merging === other.id}
                          onClick={() => merge(g, item.id, other.id)}
                          data-testid={`merge-btn-${item.id}-into-${other.id}`}
                        >
                          {merging === other.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                          Mantener "{item.name}", fusionar "{other.name}"
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
